import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const VehicleRegistrationUpload = ({ onUploadComplete }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const fileInputRef = useRef(null);

  const templateFields = [
    { field: 'Vehicle Number', required: true, example: 'MH12AB1234' },
    { field: 'Vehicle Type', required: true, example: 'Container Truck' },
    { field: 'Capacity (Tons)', required: true, example: '25' },
    { field: 'Owner Name', required: true, example: 'Transport Company Ltd' },
    { field: 'Owner Contact', required: true, example: '+91 9876543210' },
    { field: 'Owner Email', required: true, example: 'owner@transport.com' },
    { field: 'Registration Date', required: true, example: '2020-05-15' },
    { field: 'Insurance Expiry', required: true, example: '2025-12-31' },
    { field: 'RC Expiry', required: true, example: '2030-05-14' },
    { field: 'Fitness Certificate Expiry', required: true, example: '2026-05-15' },
    { field: 'Driver Name', required: true, example: 'Rajesh Kumar' },
    { field: 'Driver License Number', required: true, example: 'DL1234567890' },
    { field: 'Driver Contact', required: true, example: '+91 9876543211' },
    { field: 'Current Location', required: true, example: 'Mumbai, Maharashtra' },
    { field: 'Rate per Ton (₹)', required: true, example: '1200' },
    { field: 'GPS Enabled', required: true, example: 'Yes/No' },
    { field: 'Verified Status', required: false, example: 'Verified/Pending' },
    { field: 'Vehicle Images (URLs)', required: false, example: 'http://example.com/truck1.jpg, http://example.com/truck2.jpg' }
  ];

  const handleFileSelect = (e) => {
    const file = e?.target?.files?.[0];
    if (file) {
      validateFile(file);
    }
  };

  const handleDragOver = (e) => {
    e?.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    setIsDragging(false);
    const file = e?.dataTransfer?.files?.[0];
    if (file) {
      validateFile(file);
    }
  };

  const validateFile = (file) => {
    const errors = [];
    
    const allowedTypes = [
      'text/csv',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ];
    
    if (!allowedTypes?.includes(file?.type)) {
      errors?.push('Invalid file type. Please upload CSV or Excel (.xlsx) files only.');
    }
    
    if (file?.size > 10 * 1024 * 1024) {
      errors?.push('File size exceeds 10MB limit.');
    }
    
    if (errors?.length > 0) {
      setValidationErrors(errors);
      setSelectedFile(null);
    } else {
      setValidationErrors([]);
      setSelectedFile(file);
    }
  };

  const handleUpload = () => {
    if (!selectedFile) return;
    
    setIsUploading(true);
    setUploadProgress(0);
    
    const interval = setInterval(() => {
      setUploadProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          setIsUploading(false);
          
          const newHistoryEntry = {
            id: Date.now(),
            type: 'Vehicle Registration',
            fileName: selectedFile?.name,
            uploadDate: new Date()?.toLocaleString('en-IN'),
            status: 'success',
            totalRecords: Math?.floor(Math?.random() * 150) + 30,
            successfulRecords: Math?.floor(Math?.random() * 140) + 25,
            failedRecords: Math?.floor(Math?.random() * 10),
            uploadedBy: 'Current User'
          };
          
          onUploadComplete?.(newHistoryEntry);
          setSelectedFile(null);
          
          return 100;
        }
        return prev + 10;
      });
    }, 300);
  };

  const handleDownloadTemplate = () => {
    alert('Downloading Vehicle Registration template...\n\nThe template will include all vehicle fleet fields with example data.');
  };

  return (
    <div className="space-y-6">
      {/* Template Download Section */}
      <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <Icon name="FileDown" size={24} className="text-blue-600 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-semibold text-blue-900 mb-1">
                Download Vehicle Registration Template
              </h3>
              <p className="text-sm text-blue-700 mb-3">
                Download the pre-formatted template for vehicle fleet registration. 
                Include all truck specifications, insurance details, and driver assignments.
              </p>
              <div className="bg-white rounded-lg p-3 mb-3">
                <p className="text-xs font-semibold text-gray-700 mb-2">Template includes:</p>
                <ul className="text-xs text-gray-600 space-y-1 ml-4 list-disc">
                  <li><strong>Vehicle Specs:</strong> Number, type, capacity, registration dates</li>
                  <li><strong>Documentation:</strong> Insurance, RC, fitness certificate expiry dates</li>
                  <li><strong>Owner Details:</strong> Name, contact information</li>
                  <li><strong>Driver Assignment:</strong> Name, license, contact</li>
                  <li><strong>Operational Data:</strong> Current location, rates, GPS status</li>
                </ul>
              </div>
            </div>
          </div>
          <Button
            onClick={handleDownloadTemplate}
            className="bg-blue-600 hover:bg-blue-700 text-white whitespace-nowrap"
          >
            <Icon name="Download" size={18} />
            <span>Download Template</span>
          </Button>
        </div>
      </div>

      {/* Upload Zone */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`border-2 border-dashed rounded-xl p-8 text-center transition-all ${
          isDragging
            ? 'border-blue-500 bg-blue-50' :'border-gray-300 bg-white hover:border-blue-500 hover:bg-blue-50/30'
        }`}
      >
        {selectedFile ? (
          <div className="space-y-4">
            <div className="flex items-center justify-center gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <Icon name="FileCheck" size={32} className="text-blue-600" />
              <div className="text-left">
                <p className="font-semibold text-blue-900">{selectedFile?.name}</p>
                <p className="text-sm text-blue-700">
                  {(selectedFile?.size / 1024)?.toFixed(2)} KB
                </p>
              </div>
            </div>
            
            {isUploading && (
              <div className="space-y-2">
                <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                  <div
                    className="bg-gradient-to-r from-blue-600 to-cyan-500 h-full transition-all duration-300 rounded-full"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
                <p className="text-sm text-gray-600">
                  Uploading... {uploadProgress}%
                </p>
              </div>
            )}
            
            <div className="flex gap-3 justify-center">
              <Button
                onClick={handleUpload}
                disabled={isUploading}
                className="bg-gradient-to-r from-blue-600 to-cyan-500 text-white"
              >
                <Icon name="Upload" size={18} />
                <span>{isUploading ? 'Uploading...' : 'Upload File'}</span>
              </Button>
              <Button
                onClick={() => setSelectedFile(null)}
                variant="outline"
                disabled={isUploading}
              >
                <Icon name="X" size={18} />
                <span>Remove</span>
              </Button>
            </div>
          </div>
        ) : (
          <>
            <Icon name="Truck" size={64} className="mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Upload Vehicle Fleet Data
            </h3>
            <p className="text-gray-600 mb-4">
              Drag and drop your CSV or Excel file here, or click to browse
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={handleFileSelect}
              className="hidden"
            />
            <Button
              onClick={() => fileInputRef?.current?.click()}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              <Icon name="FolderOpen" size={18} />
              <span>Select File</span>
            </Button>
            <p className="text-xs text-gray-500 mt-3">
              Supported formats: CSV, XLSX • Maximum size: 10MB
            </p>
          </>
        )}
      </div>

      {/* Validation Errors */}
      {validationErrors?.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Icon name="AlertTriangle" size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-semibold text-red-900 mb-2">Validation Errors</h4>
              <ul className="space-y-1">
                {validationErrors?.map((error, index) => (
                  <li key={index} className="text-sm text-red-700">
                    • {error}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Field Reference */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <Icon name="List" size={18} className="text-blue-600" />
          Template Field Reference
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {templateFields?.map((field, index) => (
            <div key={index} className="bg-white rounded-lg p-3 border border-gray-200">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1">
                  <p className="text-sm font-semibold text-gray-900">
                    {field?.field}
                    {field?.required && <span className="text-red-500 ml-1">*</span>}
                  </p>
                  <p className="text-xs text-gray-600 mt-1">
                    Example: {field?.example}
                  </p>
                </div>
                {field?.required && (
                  <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs font-medium rounded">
                    Required
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Additional Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Icon name="Info" size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-semibold mb-2">Important Notes:</p>
            <ul className="space-y-1 ml-4 list-disc">
              <li>All fields marked with * are mandatory</li>
              <li>Vehicle numbers must follow standard registration format</li>
              <li>Expiry dates should be in YYYY-MM-DD format</li>
              <li>Insurance and RC documents must be valid</li>
              <li>System will verify duplicate vehicle registrations</li>
              <li>Driver license numbers will be validated</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VehicleRegistrationUpload;